<script setup lang="ts">
import { ref, computed, defineAsyncComponent } from 'vue';
import { useGeolocationStore } from '@/modules/geolocation';
import { useAlertStore } from '@/modules/alerts';
import { useSettingsStore, type PowerMode } from '@/modules/settings';
import { EXPLOSION_CLASS_COLORS, EXPLOSION_CLASS_LABELS } from '@/types/geolocation';
import type { LiveDetection, SelfTestOptions } from './useLiveDetection';

// Lazy: pulls the Spectrogrammer FFT only when the user actually opens the visualisation.
const RadarSpectrogram = defineAsyncComponent(() => import('./RadarSpectrogram.vue'));
// Lazy too: the detection chart is debug-oriented, so load it only when opened.
const RadarDetectionChart = defineAsyncComponent(() => import('./RadarDetectionChart.vue'));
// Lazy too: the Stage-0 energy chart drills into the raw RMS/flux/low-band features behind the
// cascade gate — debug-oriented, so load it only when opened (Block 5.2/5.3).
const RadarEnergyChart = defineAsyncComponent(() => import('./RadarEnergyChart.vue'));

/**
 * Панель настроек и диагностики (раскрывается из ⚙ в шапке). Чтобы не занимать
 * пол-экрана вертикальным списком, разделы сгруппированы во вкладки с иконками:
 * неактивные показывают только значок, активная — значок + подпись.
 *   📍 Локация · 🔋 Режим · 🔄 Связь · 🔧 Диагностика
 * Локация принадлежит странице (там карта и GPS) — наружу только emits;
 * power mode и спектрограмма полностью свои.
 */

const props = defineProps<{
  detection: LiveDetection;
  liveTracking: boolean;
  manualMode: boolean;
  /** 'manual' подсвечивает кнопку «Вручную» даже после выхода из режима выбора. */
  locationSource: string | null;
  manualError: string | null;
  manualApplied: boolean;
  /** Текущий интервал периодического опроса сервера (сек); 0 = только push. */
  syncIntervalSec: number;
}>();

const emit = defineEmits<{
  'toggle-live-tracking': [];
  'locate-once': [];
  'toggle-manual-mode': [];
  'apply-manual-coords': [];
  'set-sync-interval': [number];
}>();

/** Поля ввода координат — двусторонние: страница пишет в них prefill/коммит. */
const manualLat = defineModel<string>('manualLat', { required: true });
const manualLon = defineModel<string>('manualLon', { required: true });

const store = useGeolocationStore();
const alertStore = useAlertStore();
const settings = useSettingsStore();

// Build-time version metadata (injected via vite.config.ts `define`).
const appVersion = __APP_VERSION__;
const buildHash = __BUILD_HASH__;
const buildDate = __BUILD_DATE__;

// ── Tabs ──────────────────────────────────────────────────────────────────────
type TabId = 'location' | 'mode' | 'sync' | 'diag';
const TABS: { id: TabId; icon: string; label: string }[] = [
  { id: 'location', icon: '📍', label: 'Локация' },
  { id: 'mode', icon: '🔋', label: 'Режим' },
  { id: 'sync', icon: '🔄', label: 'Связь' },
  { id: 'diag', icon: '🔧', label: 'Диагностика' },
];
const activeTab = ref<TabId>('location');

function setPowerMode(mode: PowerMode): void {
  settings.setPowerMode(mode);
}

/** Stage-2 detection confidence threshold, shown/edited as a percentage (50–90 %). */
const thresholdPct = computed(() => Math.round(props.detection.stage2Threshold.value * 100));
function onThreshold(e: Event): void {
  const v = Number((e.target as HTMLInputElement).value);
  if (Number.isFinite(v)) props.detection.setStage2Threshold(v / 100);
}

// ── Server sync interval ────────────────────────────────────────────────────────
const SYNC_OPTIONS: { sec: number; label: string }[] = [
  { sec: 0, label: 'Выкл' },
  { sec: 15, label: '15 с' },
  { sec: 30, label: '30 с' },
  { sec: 60, label: '1 мин' },
  { sec: 300, label: '5 мин' },
];
const syncLabel = computed(
  () => SYNC_OPTIONS.find((o) => o.sec === props.syncIntervalSec)?.label ?? `${props.syncIntervalSec} с`,
);

/** Optional live spectrogram / audio visualisation (like admin). Off by default, persisted. */
const SPECTRO_KEY = 'pushka:showSpectro';
const showSpectrogram = ref(loadSpectroPref());
function loadSpectroPref(): boolean {
  try {
    return localStorage.getItem(SPECTRO_KEY) === '1';
  } catch {
    return false;
  }
}
function toggleSpectrogram(): void {
  showSpectrogram.value = !showSpectrogram.value;
  try {
    localStorage.setItem(SPECTRO_KEY, showSpectrogram.value ? '1' : '0');
  } catch {
    /* ignore */
  }
}

/** Optional live detection chart (confidence + onset over time). Off by default, persisted. */
const DET_CHART_KEY = 'pushka:showDetChart';
const showDetChart = ref(loadDetChartPref());
function loadDetChartPref(): boolean {
  try {
    return localStorage.getItem(DET_CHART_KEY) === '1';
  } catch {
    return false;
  }
}
function toggleDetChart(): void {
  showDetChart.value = !showDetChart.value;
  try {
    localStorage.setItem(DET_CHART_KEY, showDetChart.value ? '1' : '0');
  } catch {
    /* ignore */
  }
}

/**
 * Optional Stage-0 energy chart (RMS / flux / low-band + onset markers + armed-hold zones).
 * Off by default, persisted — like the detection chart. Surfaces the raw features behind the
 * cascade's binary Stage-0 gate, for tuning / false-positive investigation (Block 5.2/5.3).
 */
const ENERGY_CHART_KEY = 'pushka:showEnergyChart';
const showEnergyChart = ref(loadEnergyChartPref());
function loadEnergyChartPref(): boolean {
  try {
    return localStorage.getItem(ENERGY_CHART_KEY) === '1';
  } catch {
    return false;
  }
}
function toggleEnergyChart(): void {
  showEnergyChart.value = !showEnergyChart.value;
  try {
    localStorage.setItem(ENERGY_CHART_KEY, showEnergyChart.value ? '1' : '0');
  } catch {
    /* ignore */
  }
}

const diagSkipStage1 = ref(false);
const diagSkipStage3 = ref(false);
const diagFreshPipeline = ref(true);
const diagNoSuppression = ref(false);

function runDiag(): void {
  const opts: SelfTestOptions = {
    skipStage1: diagSkipStage1.value,
    skipStage3: diagSkipStage3.value,
    freshPipeline: diagFreshPipeline.value,
    noSuppression: diagNoSuppression.value,
  };
  props.detection.runSelfTest(opts);
}
</script>

<template>
  <div class="tools-panel">
    <!-- Tab bar: inactive tabs show only the icon, the active one also shows its label -->
    <div class="tool-tabs">
      <button
        v-for="t in TABS"
        :key="t.id"
        class="tool-tab"
        :class="{ active: activeTab === t.id }"
        :title="t.label"
        @click="activeTab = t.id"
      >
        <span class="tool-tab-icon">{{ t.icon }}</span>
        <span
          v-if="activeTab === t.id"
          class="tool-tab-label"
        >{{ t.label }}</span>
      </button>
    </div>

    <!-- ── Tab: Локация ── -->
    <div v-show="activeTab === 'location'">
      <!-- Location mode: auto (continuous tracking) / once / manual -->
      <div class="loc-bar">
        <button
          class="loc-btn"
          :class="{ active: liveTracking }"
          @click="emit('toggle-live-tracking')"
        >
          🛰 Авто
        </button>
        <button
          class="loc-btn"
          :class="{ active: !liveTracking && !manualMode && locationSource !== 'manual' }"
          @click="emit('locate-once')"
        >
          📍 Один раз
        </button>
        <button
          class="loc-btn"
          :class="{ active: manualMode || locationSource === 'manual' }"
          @click="emit('toggle-manual-mode')"
        >
          ✋ Вручную
        </button>
      </div>

      <!-- Mode status: highlight that the position is fixed (battery saving) unless auto is on -->
      <div
        class="loc-status"
        :class="{ tracking: liveTracking }"
      >
        {{
          liveTracking
            ? '🛰 Постоянное отслеживание включено'
            : '🔒 Позиция зафиксирована · экономия батареи'
        }}
      </div>

      <!-- Manual placement: tap the map or type coordinates. The editor is shown only while the
           manual mode is armed; after committing we briefly confirm, then collapse. -->
      <div
        v-if="manualMode || manualApplied"
        class="manual-coords"
      >
        <template v-if="manualMode">
          <div class="manual-hint">
            Коснитесь карты или введите координаты вручную:
          </div>
          <div class="manual-fields">
            <input
              v-model="manualLat"
              class="manual-input"
              type="text"
              inputmode="decimal"
              placeholder="Широта"
              @keyup.enter="emit('apply-manual-coords')"
            >
            <input
              v-model="manualLon"
              class="manual-input"
              type="text"
              inputmode="decimal"
              placeholder="Долгота"
              @keyup.enter="emit('apply-manual-coords')"
            >
            <button
              class="manual-apply"
              @click="emit('apply-manual-coords')"
            >
              OK
            </button>
          </div>
          <div
            v-if="manualError"
            class="manual-error"
          >
            {{ manualError }}
          </div>
        </template>
        <div
          v-else
          class="manual-applied"
        >
          ✓ Координаты применены · {{ manualLat }}, {{ manualLon }}
        </div>
      </div>
    </div>

    <!-- ── Tab: Режим (power saving + sensitivity) ── -->
    <div v-show="activeTab === 'mode'">
      <!-- Power mode: how the detector reacts to air-raid alerts around the user -->
      <div class="power-title">
        Энергосбережение по воздушной тревоге
      </div>
      <div class="loc-bar">
        <button
          class="loc-btn"
          :class="{ active: settings.powerMode === 'normal' }"
          @click="setPowerMode('normal')"
        >
          🔋 Обычный
        </button>
        <button
          class="loc-btn"
          :class="{ active: settings.powerMode === 'adaptive' }"
          @click="setPowerMode('adaptive')"
        >
          ⚡ Адаптивный
        </button>
        <button
          class="loc-btn"
          :class="{ active: settings.powerMode === 'alertOnly' }"
          @click="setPowerMode('alertOnly')"
        >
          🌙 По тревоге
        </button>
      </div>
      <div class="power-hint">
        {{
          settings.powerMode === 'alertOnly'
            ? '🌙 Детектор слушает только во время воздушной тревоги рядом (~50 км) — максимальная экономия.'
            : settings.powerMode === 'adaptive'
              ? '⚡ Без тревоги детектор менее чувствителен (реже будит тяжёлую модель) — экономия батареи.'
              : '🔋 Детектор работает одинаково всегда, статус тревоги не учитывается.'
        }}
      </div>
      <div
        v-if="settings.powerMode !== 'normal' && alertStore.alertActive === null"
        class="power-warn"
      >
        ⚠ Статус тревоги неизвестен ({{ store.userCenter ? 'нет данных от сервера' : 'не определено местоположение' }}) —
        детектор работает в обычном режиме.
      </div>

      <!-- Detection sensitivity: Stage-2 classifier confidence threshold (50–90 %) -->
      <div class="power-title">
        Чувствительность детектора
      </div>
      <div class="thr-row">
        <input
          class="thr-range"
          type="range"
          min="50"
          max="90"
          step="5"
          :value="thresholdPct"
          @input="onThreshold"
        >
        <span class="thr-val">{{ thresholdPct }}%</span>
      </div>
      <div class="power-hint">
        Минимальная уверенность модели, чтобы засчитать срабатывание. Выше порог — меньше ложных
        тревог, но можно пропустить слабые/далёкие события.
      </div>
    </div>

    <!-- ── Tab: Связь (server sync interval) ── -->
    <div v-show="activeTab === 'sync'">
      <div class="power-title">
        Интервал синхронизации с сервером
      </div>
      <div class="loc-bar sync-bar">
        <button
          v-for="o in SYNC_OPTIONS"
          :key="o.sec"
          class="loc-btn"
          :class="{ active: syncIntervalSec === o.sec }"
          @click="emit('set-sync-interval', o.sec)"
        >
          {{ o.label }}
        </button>
      </div>
      <div class="power-hint">
        {{
          syncIntervalSec === 0
            ? '🔌 Радар обновляется только в реальном времени (push с сервера). Периодический опрос выключен — меньше трафика.'
            : `🔄 Раз в ${syncLabel} радар дополнительно перезапрашивает события вокруг вас — подстраховка на случай пропущенных push-обновлений.`
        }}
      </div>

      <!-- Clock-sync probe: how far this device's clock is from the server (matters for TDOA) -->
      <div class="power-title">
        Синхронизация времени
      </div>
      <button
        class="diag-btn"
        :disabled="detection.clockCheckRunning.value"
        @click="detection.checkClockSync()"
      >
        <span>🕒 Проверить расхождение с сервером</span>
        <span
          v-if="detection.clockCheckRunning.value"
          class="diag-state"
        >…</span>
      </button>
      <div
        v-if="detection.clockCheckError.value"
        class="monitor-error"
      >
        ⚠ {{ detection.clockCheckError.value }}
      </div>
      <div
        v-else-if="detection.clockCheckResult.value"
        class="clock-panel"
        :class="{ warn: Math.abs(detection.clockCheckResult.value.offsetMs) > 100 }"
      >
        <div class="clock-offset">
          {{ detection.clockCheckResult.value.offsetMs > 0 ? '+' : '' }}{{ detection.clockCheckResult.value.offsetMs }} мс
          <span class="clock-verdict">
            {{ Math.abs(detection.clockCheckResult.value.offsetMs) > 100 ? 'часы заметно расходятся' : 'часы в норме' }}
          </span>
        </div>
        <div class="clock-meta">
          Часы устройства {{ detection.clockCheckResult.value.offsetMs > 0 ? 'отстают' : 'спешат' }} относительно сервера
          <span v-if="detection.clockCheckResult.value.rttMs !== null">
            · точность ±{{ Math.round(detection.clockCheckResult.value.rttMs / 2) }} мс
          </span>
        </div>
      </div>
      <div class="power-hint">
        ⏱ Радар сам подстраивает время каждого события под часы сервера — это нужно, чтобы триангуляция
        по нескольким устройствам не сбивалась. Кнопка показывает текущее расхождение ваших часов.
      </div>
    </div>

    <!-- ── Tab: Диагностика (visualisations + self-test) ── -->
    <div v-show="activeTab === 'diag'">
      <!-- Optional live spectrogram / audio visualisation -->
      <button
        class="spectro-toggle"
        :class="{ on: showSpectrogram }"
        @click="toggleSpectrogram"
      >
        <span>📹 Спектрограмма и аудио</span>
        <span class="spectro-state">{{ showSpectrogram ? 'вкл' : 'выкл' }}</span>
      </button>
      <RadarSpectrogram v-if="detection.monitoring.value && showSpectrogram" />
      <div
        v-else-if="showSpectrogram"
        class="spectro-hint"
      >
        Включите мониторинг, чтобы увидеть спектрограмму и уровень звука.
      </div>

      <!-- Optional live detection chart (confidence + onset over time) -->
      <button
        class="spectro-toggle"
        :class="{ on: showDetChart }"
        @click="toggleDetChart"
      >
        <span>📈 График детекции</span>
        <span class="spectro-state">{{ showDetChart ? 'вкл' : 'выкл' }}</span>
      </button>
      <RadarDetectionChart
        v-if="detection.monitoring.value && showDetChart"
        :detection="detection"
      />
      <div
        v-else-if="showDetChart"
        class="spectro-hint"
      >
        Включите мониторинг, чтобы увидеть уровень срабатывания детектора по времени.
      </div>

      <!-- Optional Stage-0 energy chart (raw RMS/flux/low-band features behind the gate) -->
      <button
        class="spectro-toggle"
        :class="{ on: showEnergyChart }"
        @click="toggleEnergyChart"
      >
        <span>⚡ График энергии</span>
        <span class="spectro-state">{{ showEnergyChart ? 'вкл' : 'выкл' }}</span>
      </button>
      <RadarEnergyChart
        v-if="detection.monitoring.value && showEnergyChart"
        :detection="detection"
      />
      <div
        v-else-if="showEnergyChart"
        class="spectro-hint"
      >
        Включите мониторинг, чтобы увидеть график энергии Stage-0 (RMS, флюкс, низкие частоты).
      </div>

      <!-- Self-diagnostic: prove the detector works on a bundled reference explosion -->
      <div class="diag-opts">
        <label class="diag-opt">
          <input
            v-model="diagFreshPipeline"
            type="checkbox"
          >
          <span>Чистый запуск</span>
        </label>
        <label class="diag-opt">
          <input
            v-model="diagSkipStage1"
            type="checkbox"
          >
          <span>Без быстрого фильтра</span>
        </label>
        <label class="diag-opt">
          <input
            v-model="diagSkipStage3"
            type="checkbox"
          >
          <span>Без уточнения</span>
        </label>
        <label class="diag-opt">
          <input
            v-model="diagNoSuppression"
            type="checkbox"
          >
          <span>Без подавления</span>
        </label>
      </div>
      <div class="diag-opts-hint">
        {{
          diagFreshPipeline && !diagNoSuppression
            ? 'Результат стабилен между запусками (состояние сбрасывается)'
            : diagNoSuppression
              ? 'Показаны все детекции без фильтрации повторов и хвостов'
              : 'Результат может отличаться (накопленное состояние детектора)'
        }}
      </div>
      <!-- Audio clip storage quality: lossless WAV (default) vs Opus compression -->
      <button
        class="spectro-toggle"
        :class="{ on: settings.audioClipCompression }"
        @click="settings.setAudioClipCompression(!settings.audioClipCompression)"
      >
        <span>💾 Сжимать записи (Opus)</span>
        <span class="spectro-state">{{ settings.audioClipCompression ? 'вкл' : 'выкл' }}</span>
      </button>
      <div class="spectro-hint">
        {{
          settings.audioClipCompression
            ? 'Записи сжимаются (Opus, ~8× меньше места), но звук теряет точность — выгрузка «для анализа» не совпадёт с тем, что услышала модель.'
            : 'Записи хранятся без потерь (WAV 16 кГц) — выгрузка «для анализа» байт-в-байт совпадает со входом детектора. Занимает больше места.'
        }}
      </div>
      <!-- Auto-upload of captured clips (the same opt-in privacy setting as the admin
           SettingsPanel; the upload path itself lives in useLiveDetection.scheduleAudioClip) -->
      <button
        class="spectro-toggle"
        :class="{ on: settings.audioClipAutoUpload }"
        @click="settings.setAudioClipAutoUpload(!settings.audioClipAutoUpload)"
      >
        <span>📤 Авто-отправка записей</span>
        <span class="spectro-state">{{ settings.audioClipAutoUpload ? 'вкл' : 'выкл' }}</span>
      </button>
      <div class="spectro-hint">
        {{
          settings.audioClipAutoUpload
            ? 'Каждая запись автоматически уходит на сервер с полным отчётом детекции (для сбора данных полевых сессий и разбора ложных срабатываний).'
            : 'Записи остаются только на устройстве; на сервер клип уходит лишь по кнопке «Отправить для анализа».'
        }}
      </div>

      <button
        class="diag-btn"
        :disabled="detection.selfTestRunning.value"
        @click="runDiag()"
      >
        <span>🔧 Самодиагностика детектора</span>
        <span
          v-if="detection.selfTestRunning.value"
          class="diag-state"
        >
          {{ Math.round(detection.selfTestProgress.value * 100) }}%
        </span>
      </button>
      <div
        v-if="detection.selfTestError.value"
        class="monitor-error"
      >
        ⚠ {{ detection.selfTestError.value }}
      </div>
      <div
        v-if="detection.selfTestResult.value"
        class="diag-panel"
        :class="{ ok: detection.selfTestResult.value.pass }"
      >
        <div class="diag-verdict">
          {{ detection.selfTestResult.value.pass ? '✅ Детектор работает' : '❌ Проблема с детекцией' }}
        </div>
        <div
          v-for="a in detection.selfTestResult.value.assertions"
          :key="a.label"
          class="diag-row"
        >
          <span class="diag-mark">{{ a.pass ? '✓' : '✗' }}</span>
          <div class="diag-body">
            <div class="diag-line">
              <span class="diag-label">{{ a.label }}
                <span class="diag-range">эталон {{ a.startSec }}–{{ a.endSec }} c</span>
              </span>
              <span class="diag-found">
                <template v-if="a.bestClass">
                  {{ EXPLOSION_CLASS_LABELS[a.bestClass] || a.bestClass }} · {{ (a.bestConfidence * 100).toFixed(0) }}%
                </template>
                <template v-else>не обнаружено</template>
              </span>
            </div>
            <div
              v-if="a.bestTimeSec !== null"
              class="diag-timing"
            >
              обнаружено в {{ a.bestTimeSec.toFixed(2) }} c
              <template v-if="a.expectedOnsetSec !== null">
                · эталон {{ a.expectedOnsetSec.toFixed(2) }} c ·
                <span :class="['diag-err', Math.abs(a.onsetErrorMs || 0) <= 1000 ? 'ok' : 'warn']">
                  расхождение {{ (a.onsetErrorMs || 0) > 0 ? '+' : '' }}{{ a.onsetErrorMs }} мс
                </span>
              </template>
            </div>
          </div>
        </div>

        <!-- Когда что сработало: полная лента детекций по времени -->
        <div
          v-if="detection.selfTestResult.value.events.length"
          class="diag-events"
        >
          <div class="diag-events-title">
            Детекции по времени
          </div>
          <div
            v-for="(ev, i) in detection.selfTestResult.value.events"
            :key="i"
            class="diag-event"
          >
            <span class="diag-event-time">{{ ev.timeSec.toFixed(2) }} c</span>
            <span
              class="diag-event-class"
              :style="{ color: EXPLOSION_CLASS_COLORS[ev.className] }"
            >{{ EXPLOSION_CLASS_LABELS[ev.className] || ev.className }}</span>
            <span class="diag-event-conf">{{ (ev.confidence * 100).toFixed(0) }}%</span>
            <span
              v-if="ev.fromOnset"
              class="diag-event-onset"
              title="время из onset-головы модели (субкадровая точность)"
            >onset</span>
          </div>
        </div>

        <div class="diag-meta">
          Детекций: {{ detection.selfTestResult.value.detections }}
          · модель: {{ detection.selfTestResult.value.stage2Model }}
          <template v-if="detection.selfTestResult.value.optionsUsed.skipStage1">
            · без С1
          </template>
          <template v-if="detection.selfTestResult.value.optionsUsed.skipStage3">
            · без С3
          </template>
          <template v-if="detection.selfTestResult.value.optionsUsed.noSuppression">
            · без подавления
          </template>
          <template v-if="detection.selfTestResult.value.optionsUsed.freshPipeline">
            · чистый запуск
          </template>
        </div>
      </div>
    </div>

    <!-- App version / build metadata (always visible under the tabs) -->
    <div
      class="app-version"
      :title="`Версия ${appVersion} · сборка ${buildHash} от ${buildDate}`"
    >
      v{{ appVersion }} · {{ buildHash }} · {{ buildDate }}
    </div>
  </div>
</template>

<style scoped>
/* ── Settings / diagnostics (collapsible; toggled from the header ⚙ button) ── */
.tools-panel {
  flex-shrink: 0;
  max-height: 50vh;
  overflow-y: auto;
  position: relative;
  z-index: 1;
}

/* ── Tab bar: icon-only chips; the active tab expands to icon + label ── */
.tool-tabs {
  display: flex;
  gap: 6px;
  padding: 10px 12px 0;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}
.tool-tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 11px;
  border-radius: 10px;
  border: 1px solid #243049;
  background: #161e2c;
  color: #aeb8cc;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: flex-grow 0.15s ease;
}
.tool-tab.active {
  flex: 1;
  background: rgba(42, 171, 238, 0.16);
  border-color: #2aabee;
  color: #cde9fb;
}
.tool-tab-icon {
  font-size: 15px;
  line-height: 1;
}
.tool-tab-label {
  white-space: nowrap;
}

/* ── Location controls ── */
.loc-bar {
  display: flex;
  gap: 8px;
  padding: 10px 12px 0;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}
/* Sync interval has five short options — let them wrap on very narrow screens. */
.sync-bar {
  flex-wrap: wrap;
}
.loc-btn {
  flex: 1;
  padding: 9px 10px;
  border-radius: 10px;
  border: 1px solid #243049;
  background: #161e2c;
  color: #aeb8cc;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.loc-btn.active {
  background: rgba(42, 171, 238, 0.16);
  border-color: #2aabee;
  color: #cde9fb;
}
/* Mode status line: fixed (battery saving) vs continuous tracking. */
.loc-status {
  margin: 8px 12px 0;
  padding: 7px 11px;
  border-radius: 9px;
  font-size: 12.5px;
  font-weight: 600;
  background: rgba(241, 196, 15, 0.12);
  color: #f1c40f;
  border: 1px solid rgba(241, 196, 15, 0.3);
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}
.loc-status.tracking {
  background: rgba(42, 171, 238, 0.14);
  color: #9bd6f7;
  border-color: rgba(42, 171, 238, 0.4);
}

/* ── Power mode (air-raid alert based battery saving) ── */
.power-title {
  margin: 14px 12px 0;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #8b96ab;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}
.power-hint {
  margin: 8px 12px 0;
  padding: 7px 11px;
  border-radius: 9px;
  font-size: 12.5px;
  color: #aeb8cc;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid #243049;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}
.power-warn {
  margin: 8px 12px 0;
  padding: 7px 11px;
  border-radius: 9px;
  font-size: 12.5px;
  font-weight: 600;
  background: rgba(241, 196, 15, 0.12);
  color: #f1c40f;
  border: 1px solid rgba(241, 196, 15, 0.3);
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}

/* ── Detection sensitivity slider ── */
.thr-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 8px 12px 0;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}
.thr-range {
  flex: 1;
  min-width: 0;
  accent-color: #2aabee;
  cursor: pointer;
}
.thr-val {
  flex-shrink: 0;
  min-width: 42px;
  text-align: right;
  font-size: 14px;
  font-weight: 700;
  color: #cde9fb;
  font-variant-numeric: tabular-nums;
}

/* ── Manual coordinate input ── */
.manual-coords {
  margin: 8px 12px 0;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}
.manual-hint {
  font-size: 12px;
  color: #8b94a7;
  margin-bottom: 6px;
}
.manual-fields {
  display: flex;
  gap: 6px;
}
.manual-input {
  flex: 1;
  min-width: 0;
  padding: 8px 10px;
  border-radius: 9px;
  border: 1px solid #243049;
  background: #0f1726;
  color: #e6e9ef;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}
.manual-input:focus {
  outline: none;
  border-color: #2aabee;
}
.manual-input::placeholder {
  color: #5a6478;
}
.manual-apply {
  flex-shrink: 0;
  padding: 8px 16px;
  border-radius: 9px;
  border: none;
  background: #2aabee;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.manual-apply:active {
  background: #1f8fd0;
}
.manual-error {
  margin-top: 6px;
  font-size: 12px;
  color: #ff8a8a;
}
.manual-applied {
  padding: 8px 11px;
  border-radius: 9px;
  font-size: 12.5px;
  font-weight: 600;
  background: rgba(46, 204, 113, 0.14);
  color: #8fe6b3;
  border: 1px solid rgba(46, 204, 113, 0.4);
}

/* ── Spectrogram toggle ── */
.spectro-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 8px 12px 0;
  padding: 9px 12px;
  border: 1px solid #243049;
  border-radius: 10px;
  background: #161e2c;
  color: #aeb8cc;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}
.spectro-toggle.on {
  background: rgba(42, 171, 238, 0.16);
  border-color: #2aabee;
  color: #cde9fb;
}
.spectro-state {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  opacity: 0.7;
  margin-left: 12px;
}
.spectro-hint {
  margin: 8px 12px 0;
  padding: 10px 12px;
  background: #161e2c;
  border: 1px dashed #243049;
  border-radius: 10px;
  color: #8b94a7;
  font-size: 12.5px;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}

/* ── Clock-sync probe result ── */
.clock-panel {
  margin: 8px 12px 0;
  padding: 10px 12px;
  background: #161e2c;
  border: 1px solid #1f7a44;
  border-radius: 10px;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}
.clock-panel.warn {
  border-color: rgba(241, 196, 15, 0.5);
}
.clock-offset {
  display: flex;
  align-items: baseline;
  gap: 10px;
  font-size: 18px;
  font-weight: 700;
  color: #cde9fb;
  font-variant-numeric: tabular-nums;
}
.clock-panel.warn .clock-offset {
  color: #f1c40f;
}
.clock-verdict {
  font-size: 12px;
  font-weight: 600;
  color: #8b94a7;
}
.clock-meta {
  margin-top: 4px;
  font-size: 11.5px;
  color: #8b94a7;
  font-variant-numeric: tabular-nums;
}

/* Ошибка самодиагностики (same look as the page-level monitor error). */
.monitor-error {
  margin: 8px 12px 0;
  padding: 8px 12px;
  background: #2a1414;
  color: #ff8a8a;
  border-radius: 8px;
  font-size: 12px;
  flex-shrink: 0;
}

/* ── Self-diagnostic ── */
.diag-opts {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin: 8px 12px 0;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}
.diag-opt {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: #aeb8cc;
  cursor: pointer;
  padding: 5px 9px;
  border-radius: 8px;
  border: 1px solid #243049;
  background: #161e2c;
  user-select: none;
}
.diag-opt:hover {
  border-color: #3a4a66;
}
.diag-opt input[type="checkbox"] {
  accent-color: #2aabee;
  margin: 0;
  width: 14px;
  height: 14px;
}
.diag-opts-hint {
  margin: 6px 12px 0;
  font-size: 11px;
  color: #6f7a90;
  flex-shrink: 0;
}
.diag-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 8px 12px 0;
  padding: 9px 12px;
  border: 1px solid #243049;
  border-radius: 10px;
  background: #161e2c;
  color: #aeb8cc;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}
.diag-btn:disabled {
  opacity: 0.7;
  cursor: default;
}
.diag-state {
  font-size: 11px;
  font-weight: 700;
  color: #2aabee;
  font-variant-numeric: tabular-nums;
}
.diag-panel {
  margin: 8px 12px 0;
  padding: 10px 12px;
  background: #161e2c;
  border: 1px solid #7a2a2a;
  border-radius: 10px;
  font-size: 12.5px;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}
.diag-panel.ok {
  border-color: #1f7a44;
}
.diag-verdict {
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 8px;
}
.diag-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 4px 0;
}
.diag-mark {
  flex-shrink: 0;
  font-weight: 700;
  line-height: 1.5;
}
.diag-body {
  flex: 1;
  min-width: 0;
}
.diag-line {
  display: flex;
  align-items: baseline;
  gap: 8px;
}
.diag-label {
  flex: 1;
  color: #cdd5e3;
}
.diag-range {
  color: #6f7a90;
  font-size: 11px;
  margin-left: 4px;
}
.diag-found {
  color: #9bd6f7;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.diag-timing {
  margin-top: 2px;
  color: #8b94a7;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}
.diag-err.ok {
  color: #5fd08a;
}
.diag-err.warn {
  color: #f1c40f;
}
.diag-events {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid #243049;
}
.diag-events-title {
  color: #6f7a90;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 4px;
}
.diag-event {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 2px 0;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}
.diag-event-time {
  color: #aeb8cc;
  width: 56px;
  flex-shrink: 0;
}
.diag-event-class {
  flex: 1;
  font-weight: 600;
}
.diag-event-conf {
  color: #9bd6f7;
}
.diag-event-onset {
  color: #6f7a90;
  font-size: 10px;
  border: 1px solid #2a3346;
  border-radius: 6px;
  padding: 0 5px;
}
.diag-meta {
  margin-top: 10px;
  color: #6f7a90;
  font-size: 11px;
}

/* ── App version / build metadata ── */
.app-version {
  margin: 14px 12px 12px;
  text-align: center;
  font-size: 10px;
  color: #6f7a90;
  letter-spacing: 0.02em;
  font-variant-numeric: tabular-nums;
  user-select: text;
  cursor: default;
}
</style>
